import { IStdImage } from './StdImage';
import { IUser, User, UserRole } from './TeamMemberStore';
import { flow, getEnv, getRoot, types } from 'mobx-state-tree';
import { IRootEnv, RootModelType } from './Root';
import { OrganisationReference, IOrganisation } from './OrganisationStore';

export type IInvitationData = {
  key: string;
  created_at: string;
  key_expired: boolean;
  sent: string;
  email: string;
  has_user: boolean;
  inviter_name: string;
  organisation_name: string;
  organisation_thumbnail?: IStdImage;
};

export const AuthStore = types
  .model('AuthStore', {
    me: types.maybeNull(types.late(() => User)), // TODO: Is this model the same as a user?
    authToken: types.maybe(types.string),
    currentOrganisation: types.maybeNull(
      types.late(() => OrganisationReference)
    ),
    currentMembership: types.maybeNull(UserRole),
  })
  .views((self) => ({
    get isSignedIn() {
      return !!self.authToken && self.me !== null;
    },
    get env() {
      return getEnv<IRootEnv>(self);
    },
    /**
     * To be authorized, one must have a token, have fetched `me` and also selected an organisation.
     */
    get isAuthorized() {
      return this.isSignedIn && self.currentOrganisation !== null;
    },
  }))
  .actions((self) => ({
    identify() {
      // Identify with segment
      if (!self.me) return;

      const me = self.me;
      const roles = self.currentMembership
        ? {
            is_admin: self.currentMembership.is_admin,
            is_salesman: self.currentMembership.is_salesman,
            is_purchaser: self.currentMembership.is_purchaser,
            is_warehouse_employee: self.currentMembership.is_warehouse_employee,
          }
        : {};
      analytics.identify(
        me.id + '',
        {
          id: me.id,
          name: me.name,
          firstName: me.first_name,
          lastName: me.last_name,
          email: me.email,
          language: self.env.language.split('-')[0].toLowerCase(),

          completed_quick_start_guide:
            !self.currentOrganisation?.show_quick_start_guide,

          organisation_id: self.currentOrganisation?.id,
          has_active_subscription:
            self.currentOrganisation?.subscription?.active,
          trialing: self.currentOrganisation?.subscription?.trialing,
          ...roles,
        },
        {
          context: {
            groupId: self.currentOrganisation?.id,
          },
        }
      );
    },
    /**
     * Signing out will reset root Store and route to /auth
     */
    signOut(all: boolean = false) {
      (getRoot(self) as RootModelType).reset();
      self.env.api.post(`/logout/`, {
        all,
      });
      window.location.assign('/auth');

      // Reset/sign-out of the analytics session
      analytics.reset();
    },
  }))
  .actions((self) => ({
    health: flow(function* ping() {
      const { data } = yield self.env.api.get(`/health/`);
      return data;
    }),
    getMe: flow(function* getMe() {
      try {
        const { data } = yield self.env.api.get(`/users/me/`);
        self.me = data;
        return self.me as IUser;
      } catch (err) {
        if (err.response && err.response.status === 401) {
          self.signOut();
        }
      }
    }),
    getInvitation: flow(function* getInvitation(key: string) {
      const { data } = yield self.env.api.get<IInvitationData>(
        `/invitations/${key}/`
      );
      return data;
    }),
  }))
  .actions((self) => ({
    updateMe: flow(function* updateMe(data: any) {
      yield self.env.api.put(`/users/me/`, data);
      return yield self.getMe();
    }),
    setCurrentOrganisation(organisation: IOrganisation) {
      localStorage.setItem('organisation', organisation.id + '');
      self.currentOrganisation = organisation;
      if (self.me)
        self.currentMembership =
          self.me.organisation_memberships.find(
            (m) => m.organisation === organisation.id
          ) || null;

      self.identify();

      const root = getRoot(self) as RootModelType;
      root.reset(true);
    },
    setup: flow(function* setup() {
      // Get me from userStore and organisations from organistaionStore
      const root = getRoot(self) as RootModelType;
      const [me, { items: orgs }] = yield Promise.all([
        self.getMe(),
        root.organisationStore.getAll(),
      ]);
      if (orgs.length > 0) {
        if (
          localStorage.getItem('organisation') &&
          orgs.find(
            (org: IOrganisation) =>
              org.id + '' === localStorage.getItem('organisation')
          )
        ) {
          self.currentOrganisation = orgs.find(
            (org: IOrganisation) =>
              org.id + '' === localStorage.getItem('organisation')
          )!;
        } else {
          self.currentOrganisation = orgs[0];
        }
      }

      self.identify();

      return [me, orgs];
    }),
  }))
  .actions((self) => ({
    signIn: flow(function* signIn(email: string, password: string) {
      // Sign in
      const data = yield self.env.api
        .post<{ token: string }>(`/login/`, { email, password })
        .then((res) => res.data);
      // Save authToken
      self.authToken = data.token;
    }),
    signUp: flow(function* signUp(
      requestData: {
        email: string;
        password: string;
        first_name?: string;
        last_name?: string;
        marketing?: boolean;
      },
      invitation?: string
    ) {
      self.authToken = undefined;
      const { data } = yield self.env.api.post(`/sign-up/`, {
        ...requestData,
        invitation,
      });
      self.authToken = data.token;
    }),
    acceptInvite: flow(function* acceptInvite(key: string) {
      yield self.env.api.put(`/invitations/accept/`, {
        invitation: key,
      });
      yield self.setup();
    }),
    requestPasswordReset: flow(function* requestPasswordReset(email: string) {
      const res = yield self.env.api.post(`/password/reset/request/`, {
        email,
      });
      return res.data;
    }),
    resetPassword: flow(function* resetPassword(
      newPassword: string,
      token: string
    ) {
      const res = yield self.env.api.post(`/password/reset/confirm/`, {
        new_password: newPassword,
        email_token: token,
      });
      self.authToken = res.data.token;
    }),
  }));
